import { Fragment } from 'react'
import { Card, Title, Text, Stack, SimpleGrid, Box } from '@mantine/core'
import { bandColor, bandLabel, bandLabelHeight } from './_format'
import { useFormat } from '../_units'
import { useI18n } from '../_i18n'

// Server-computed insight cards, ported from the mobile app (free/7-day scope,
// omitting the premium "Active & Quiet Periods" card). Each card is a title over
// a vertical stack of band-colored cells. Value strings are precomputed with the
// active units; the date is formatted per-cell via useFormat.

const extremeHeight = (o, fmt, tz, t) =>
  o &&
  t('insights_value_at_time', {
    value: fmt.height(o.height_cm, o.height_ft),
    time: fmt.time(o.timestamp, tz),
  })
const tidalRange = (o, fmt, t) =>
  o && `${fmt.height(o.range_cm, o.range_ft)} ${t('insights_tidal_range_label')}`
const currentExtreme = (o, fmt, tz, t) =>
  o &&
  t('insights_value_at_time', {
    value: fmt.speed(o.speed_knots),
    time: fmt.time(o.date_time, tz),
  })
const peakCurrent = (o, fmt, t) =>
  o && `${fmt.speed(o.max_current)} ${t('insights_max_current_label')}`

function Cell({ obj, title, value, heightBand }) {
  const fmt = useFormat()
  const { t } = useI18n()
  if (!obj) return null
  const band = obj.band
  return (
    <Box
      style={{
        background: bandColor(band),
        borderRadius: 8,
        padding: '22px 16px',
        color: '#fff',
        textAlign: 'center',
      }}
    >
      <Text fz="1.45rem" fw={700}>
        {title}
      </Text>
      <Text fz="1.15rem" mt={6} style={{ opacity: 0.85 }}>
        {fmt.date(obj.date)}
      </Text>
      <Text fz="1.9rem" fw={700} mt={6}>
        {value}
      </Text>
      {band && (
        <Text fz="1.15rem" mt={6} style={{ opacity: 0.9 }}>
          {heightBand ? bandLabelHeight(t, band) : bandLabel(t, band)}
        </Text>
      )}
    </Box>
  )
}

function InsightCard({ title, left, right }) {
  if (!left.obj && !right.obj) return null
  return (
    <Card withBorder padding="md" radius="md">
      <Title order={3} fz="1.7rem" ta="center" mb="sm">
        {title}
      </Title>
      <SimpleGrid cols={1} spacing="sm">
        <Cell {...left} />
        <Cell {...right} />
      </SimpleGrid>
    </Card>
  )
}

export default function Insights({ kind, insights, timezone, midSlot = null }) {
  const fmt = useFormat()
  const { t } = useI18n()

  if (!insights) {
    return (
      <Card withBorder padding="md" radius="md">
        <Title order={2} fz="1.9rem" mb="xs">
          {t('insights_7_day_overview')}
        </Title>
        <Text c="dimmed" size="sm">
          {t('no_insights_available')}
        </Text>
      </Card>
    )
  }

  const cards =
    kind === 'current'
      ? [
          {
            title: t('insights_current_extremes'),
            left: {
              obj: insights.fastest_flood,
              title: t('insights_fastest_flood'),
              value: currentExtreme(insights.fastest_flood, fmt, timezone, t),
            },
            right: {
              obj: insights.fastest_ebb,
              title: t('insights_fastest_ebb'),
              value: currentExtreme(insights.fastest_ebb, fmt, timezone, t),
            },
          },
          {
            title: t('insights_strongest_daily_currents'),
            left: {
              obj: insights.largest_day,
              title: t('insights_fastest_current'),
              value: peakCurrent(insights.largest_day, fmt, t),
            },
            right: {
              obj: insights.smallest_day,
              title: t('insights_slowest_current'),
              value: peakCurrent(insights.smallest_day, fmt, t),
            },
          },
        ]
      : [
          {
            title: t('insights_tidal_extremes'),
            left: {
              obj: insights.highest_tide,
              title: t('insights_highest_tide'),
              heightBand: true,
              value: extremeHeight(insights.highest_tide, fmt, timezone, t),
            },
            right: {
              obj: insights.lowest_tide,
              title: t('insights_lowest_tide'),
              heightBand: true,
              value: extremeHeight(insights.lowest_tide, fmt, timezone, t),
            },
          },
          {
            title: t('insights_strongest_daily_movements'),
            left: {
              obj: insights.largest_day,
              title: t('insights_largest_tide'),
              value: tidalRange(insights.largest_day, fmt, t),
            },
            right: {
              obj: insights.smallest_day,
              title: t('insights_smallest_tide'),
              value: tidalRange(insights.smallest_day, fmt, t),
            },
          },
        ]

  return (
    <Stack gap="md">
      <Title order={2} fz="1.9rem">
        {t('insights_7_day_overview')}
      </Title>
      {cards.map((c, i) => (
        <Fragment key={c.title}>
          <InsightCard {...c} />
          {i === 0 && midSlot}
        </Fragment>
      ))}
    </Stack>
  )
}
